import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Ticket,
  User,
  DollarSign,
  Wallet,
  Calendar,
  Gift,
  MapPin,
  Car,
  ShieldCheck,
  Award,
  Download,
  Loader2,
  ArrowLeft,
  Share2,
} from "lucide-react";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { api, formatCOP } from "@/lib/api";
import { RAFFLE } from "@/lib/raffleData";

export default function Receipt() {
  const { ficha } = useParams();
  const cardRef = useRef(null);
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/receipt/${encodeURIComponent(ficha)}`);
        if (!cancelled) setState({ loading: false, error: "", data });
      } catch (e) {
        if (!cancelled) {
          const msg = e?.response?.data?.detail || "No se encontró este comprobante";
          setState({ loading: false, error: msg, data: null });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [ficha]);

  const download = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const png = await toPng(cardRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: "#051A0F",
      });
      const link = document.createElement("a");
      link.download = `BETMAR-comprobante-${state.data.ficha_number}.png`;
      link.href = png;
      link.click();
      toast.success("Comprobante descargado");
    } catch {
      toast.error("No se pudo generar la imagen");
    } finally {
      setDownloading(false);
    }
  };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: "Mi comprobante BETMAR", url }); } catch { /* cancel */ }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Enlace copiado");
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#051A0F] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#F0B91B] animate-spin" />
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen bg-[#051A0F] text-[#FDFBF7] flex items-center justify-center px-4 text-center">
        <div>
          <p className="font-bebas text-3xl tracking-wider mb-2">Comprobante no disponible</p>
          <p className="text-sm text-[#FDFBF7]/60 font-outfit mb-6">{state.error}</p>
          <Link to="/" className="inline-flex items-center gap-2 text-[#F0B91B] hover:underline font-outfit">
            <ArrowLeft className="w-4 h-4" /> Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  const d = state.data;
  const saleDate = d.created_at ? new Date(d.created_at).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" }) : "";

  return (
    <div className="min-h-screen bg-[#051A0F] grain-overlay py-6 md:py-10 px-3 md:px-6" data-testid="receipt-page">
      {/* Top actions (out of the downloadable card) */}
      <div className="max-w-2xl mx-auto flex items-center justify-between mb-4">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#FDFBF7]/60 hover:text-[#F0B91B] font-outfit transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" /> Inicio
        </Link>
        <div className="flex items-center gap-2">
          <button onClick={share} className="inline-flex items-center gap-1.5 text-xs bg-[#0A2818] border border-[#F0B91B]/40 hover:border-[#F0B91B] text-[#F0B91B] px-3 py-2 rounded-lg font-bebas tracking-wider transition-colors" data-testid="receipt-share">
            <Share2 className="w-3.5 h-3.5" /> Compartir
          </button>
          <button onClick={download} disabled={downloading} className="inline-flex items-center gap-1.5 text-xs bg-[#F0B91B] hover:bg-[#FFD700] disabled:opacity-60 text-[#051A0F] px-3 py-2 rounded-lg font-bebas tracking-wider transition-all" data-testid="receipt-download">
            {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Descargar
          </button>
        </div>
      </div>

      {/* Downloadable card */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        ref={cardRef}
        className="max-w-2xl mx-auto rounded-3xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)] border-2 border-[#F0B91B]/40"
        style={{ background: "linear-gradient(180deg,#0A2818 0%,#083518 50%,#0A2818 100%)" }}
        data-testid="receipt-card"
      >
        {/* Header — logo + green sky background */}
        <div className="relative bg-gradient-to-b from-[#0A2818] via-[#0F3B21] to-[#0A2818] px-6 pt-8 pb-6 text-center">
          <div className="inline-flex flex-col items-center mb-4">
            <div className="w-20 h-20 rounded-full bg-[#F0B91B]/15 border-2 border-[#F0B91B]/60 flex items-center justify-center mb-2">
              <Ticket className="w-10 h-10 text-[#F0B91B]" strokeWidth={2} />
            </div>
            <span className="font-bebas text-3xl md:text-4xl gold-shimmer tracking-widest">BETMAR</span>
          </div>

          {/* Green banner + gold ribbon */}
          <div className="relative inline-flex items-center gap-3 px-6 py-3 rounded-xl bg-[#083518] border-2 border-[#F0B91B]/60 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.6)]">
            <div className="w-12 h-12 rounded-full bg-[#F0B91B] flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-7 h-7 text-[#051A0F]" strokeWidth={2.5} />
            </div>
            <div className="text-left">
              <div className="font-bebas text-2xl md:text-3xl text-[#FDFBF7] tracking-wider leading-none">
                ¡COMPRA EXITOSA!
              </div>
              <div className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-[#F0B91B] font-outfit font-bold mt-1">
                Tu participación ha sido confirmada
              </div>
            </div>
          </div>
        </div>

        {/* Body — light beige panel */}
        <div className="px-4 md:px-6 py-6" style={{ background: "linear-gradient(180deg,#F5EFE1 0%,#F0E6D2 100%)" }}>
          {/* Comprobante header */}
          <div className="text-center mb-5">
            <span className="inline-block bg-[#0A2818] text-[#F0B91B] font-bebas tracking-[0.2em] px-6 py-1.5 rounded-md text-sm">
              COMPROBANTE DE PARTICIPACIÓN
            </span>
          </div>

          <div className="grid md:grid-cols-[1fr_auto] gap-4 items-stretch">
            {/* Data rows */}
            <div className="space-y-2 text-[#0A2818] font-outfit">
              <DataRow icon={<Ticket className="w-4 h-4" />} label="Ficha" value={`#${d.ficha_number}`} valueBold />
              <DataRow icon={<User className="w-4 h-4" />} label="Comprador" value={d.buyer_name} valueBold />
              <DataRow icon={<DollarSign className="w-4 h-4" />} label="Valor" value={formatCOP(d.amount)} />
              <DataRow icon={<Wallet className="w-4 h-4" />} label="Pago" value={d.payment_method} />
              <DataRow icon={<User className="w-4 h-4" />} label="Vendedor" value={d.seller_name || "Administrador"} />
              <DataRow icon={<Calendar className="w-4 h-4" />} label="Sorteo" value={RAFFLE.drawDate} last />
            </div>

            {/* Right column — entrega anticipada */}
            <div className="rounded-xl bg-[#0A2818] text-[#FDFBF7] px-4 py-5 flex flex-col items-center justify-center text-center md:min-w-[180px]">
              <div className="w-10 h-10 rounded-full bg-[#F0B91B]/20 flex items-center justify-center mb-2">
                <DollarSign className="w-5 h-5 text-[#F0B91B]" />
              </div>
              <div className="font-bebas text-xl tracking-wider">ENTREGA</div>
              <div className="font-bebas text-xl gold-shimmer tracking-wider leading-none">ANTICIPADA</div>
              <div className="text-[10px] uppercase tracking-widest text-[#FDFBF7]/70 font-outfit mt-2">
                Participas por
              </div>
              <div className="font-bebas text-2xl gold-shimmer tracking-wide leading-tight">
                ${RAFFLE.earlyPrize}
              </div>
              <div className="w-full h-px bg-[#F0B91B]/30 my-2" />
              <div className="text-[9px] uppercase tracking-widest text-[#F0B91B] font-outfit font-bold">
                Fecha de entrega
              </div>
              <div className="font-bebas text-sm text-[#FDFBF7] tracking-wide mt-0.5">
                {RAFFLE.earlyDate}
              </div>
              <div className="text-[9px] uppercase tracking-widest text-[#F0B91B]/80 font-outfit mt-0.5">
                {RAFFLE.earlyDateNote}
              </div>
            </div>
          </div>

          {/* Premios principales */}
          <div className="mt-5 text-center">
            <div className="inline-flex items-center gap-2 bg-[#0A2818] text-[#F0B91B] font-bebas tracking-[0.2em] px-6 py-1.5 rounded-md text-sm">
              <Gift className="w-4 h-4" /> PREMIOS PRINCIPALES
            </div>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-xl bg-white/60 border border-[#0A2818]/15 p-3 flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[#0A2818] flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5 text-[#F0B91B]" />
              </div>
              <div>
                <div className="font-bebas text-lg text-[#0A2818] tracking-wider leading-none">LOTE 208 m²</div>
                <div className="text-[10px] uppercase tracking-widest text-[#0A2818]/70 font-outfit font-bold mt-1">
                  En Acacías, Meta
                </div>
              </div>
            </div>
            <div className="rounded-xl bg-white/60 border border-[#0A2818]/15 p-3 flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[#0A2818] flex items-center justify-center shrink-0">
                <Car className="w-5 h-5 text-[#F0B91B]" />
              </div>
              <div>
                <div className="font-bebas text-lg text-[#0A2818] tracking-wider leading-none">CHEVROLET</div>
                <div className="font-bebas text-base text-[#0A2818]/85 tracking-wider leading-tight">Spark Life 2015</div>
              </div>
            </div>
          </div>

          {/* Gracias */}
          <div className="mt-6 text-center">
            <div className="font-bebas text-2xl text-[#0A2818] tracking-wider">¡Gracias por participar!</div>
            <p className="text-xs text-[#0A2818]/70 font-outfit mt-1 max-w-md mx-auto leading-relaxed">
              Conserva este comprobante. Es tu respaldo y acredita tu participación.
            </p>
            {saleDate && (
              <p className="text-[10px] uppercase tracking-widest text-[#0A2818]/50 font-outfit mt-2">
                Emitido: {saleDate}
              </p>
            )}
          </div>
        </div>

        {/* Footer strip */}
        <div className="bg-[#0A2818] text-[#FDFBF7] grid grid-cols-3 gap-2 px-3 py-3 text-center text-[10px] md:text-xs font-outfit">
          <FooterItem icon={<ShieldCheck className="w-4 h-4" />} title="Compra segura" subtitle="Tu confianza es nuestra prioridad" />
          <FooterItem icon={<CheckCircle2 className="w-4 h-4" />} title="Sorteo transparente" subtitle="Y garantizado" />
          <FooterItem icon={<Award className="w-4 h-4" />} title="BETMAR" subtitle="Hacemos tus sueños realidad" />
        </div>
      </motion.div>

      <p className="max-w-2xl mx-auto text-center text-[10px] uppercase tracking-widest text-[#FDFBF7]/40 font-outfit mt-6">
        Comprobante emitido por BETMAR · {RAFFLE.brand.toLowerCase()}
      </p>
    </div>
  );
}

function DataRow({ icon, label, value, valueBold = false, last = false }) {
  return (
    <div className={`flex items-center gap-3 py-1.5 ${last ? "" : "border-b border-[#0A2818]/12"}`}>
      <div className="w-7 h-7 rounded-full bg-[#F0B91B] text-[#0A2818] flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[9px] uppercase tracking-widest text-[#0A2818]/60 font-outfit font-bold leading-none">{label}</div>
        <div className={`text-sm md:text-base text-[#0A2818] ${valueBold ? "font-bold" : ""} truncate`}>{value}</div>
      </div>
    </div>
  );
}

function FooterItem({ icon, title, subtitle }) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <div className="text-[#F0B91B]">{icon}</div>
      <div className="font-bebas tracking-wider text-[#F0B91B]">{title}</div>
      <div className="text-[8px] md:text-[9px] uppercase tracking-widest text-[#FDFBF7]/60 leading-tight text-center">{subtitle}</div>
    </div>
  );
}
